import { defineStore } from "pinia";
import { computed, ref } from "vue";
import { useBreakGlass } from "@/app/break-glass";
import { queryClient } from "@/app/query-client";
import { setToken } from "@/shared/api-fetch";
import { clearActivity } from "./idle-lock";
import type { StaffRole } from "@/shared";

export interface Staff {
  id: string;
  username: string;
  role: StaffRole;
  casinoId: string | null;
}

/**
 * The session, and nothing else.
 *
 * This is the ONLY Pinia store that touches identity, and it holds no server data — Vue Query owns
 * that. `role` and `casinoId` come from `GET /v1/me`, never from the token: the token carries
 * `{staffId}` alone, and the API re-resolves both on every request. A session that decoded a role
 * would be making an authorization decision the server has already made differently.
 */
export const useSession = defineStore("session", () => {
  const staff = ref<Staff | null>(null);

  function adopt(next: Staff): void {
    staff.value = next;
  }

  /**
   * Drops the adopted identity WITHOUT touching the token, AND clears every cached query.
   *
   * For a caller that is already handling the token itself and must not repeat that side effect:
   * a rejected rehydration (`features/auth/rehydrate.ts` has already decided whether the token
   * survives a failed `/v1/me`) and another tab's `storage` event (`app/storage-sync.ts` has
   * already mirrored whatever that tab wrote). `logout()` is the user-initiated path, where
   * clearing the token IS part of the action — this is the half of it a caller can reuse alone.
   *
   * **`queryClient.clear()` belongs here, not bolted onto each caller separately.** Every path that
   * ends up here is "the identity that scoped whatever is cached just went away" — a sign-out, a
   * dead token from a central 401, or another tab's token changing out from under this one — and
   * `docs/UNOBSERVED.md` §1.3 is exactly a stale cache surviving one of those. Vue Query's own
   * server state and this store's identity are never supposed to overlap (see this file's own top
   * comment), but their LIFETIMES must: server data scoped to a session must not outlive it.
   *
   * **Break-glass ends here too, for the same reason.** The mode is scoped to the admin who entered
   * it (step-up, audit record). A sign-out is not a reload, so the Pinia store survives it; without
   * this, the next person to sign in on the tab lands inside another Casino's cash workspace with
   * no step-up and no audit record. Every path that ends a session — `logout()`, the central 401
   * (`app/session-guard.ts`), another tab's token change (`app/storage-sync.ts`) — funnels through
   * here, so this is the one place it cannot be missed. `break-glass.ts` imports nothing from auth,
   * so the import is one-way and cycle-free.
   */
  function clear(): void {
    staff.value = null;
    useBreakGlass().leave();
    queryClient.clear();
  }

  function logout(): void {
    // Clear the module-level token AND storage too. Clearing only the store leaves the next
    // request — and, with persistence, the next reload or the next tab — authenticating as the
    // person who just left.
    clear();
    setToken(null);
    clearActivity();
  }

  return {
    staff,
    role: computed(() => staff.value?.role ?? null),
    casinoId: computed(() => staff.value?.casinoId ?? null),
    isAuthed: computed(() => staff.value !== null),
    adopt,
    clear,
    logout,
  };
});
